import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  clearProfileImageService,
  deleteUserService,
  getCurrentUserService,
  uploadAndSetProfileImageService,
} from "../api/services";
import { UserAvatar } from "../components/UserAvatar";
import { useToast } from "../components/Toast";
import { useRequestError } from "../hooks/useRequestError";
import { useAuthStore } from "../stores/authStore";
import type { UserModel } from "../types/models";
import { formatDisplayName } from "../utils/name";

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();
  const fileRef = useRef<HTMLInputElement>(null);
  const [user, setUser] = useState<UserModel | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setUser(await getCurrentUserService());
    } catch (error) {
      handleRequestError(error);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const onUpload = async (file?: File | null) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadAndSetProfileImageService(file);
      await load();
      showToast({
        type: "success",
        title: "Success",
        message: "Profile picture updated.",
      });
    } catch (error) {
      handleRequestError(error);
    } finally {
      setBusy(false);
    }
  };

  const onRemovePhoto = async () => {
    setBusy(true);
    try {
      await clearProfileImageService();
      await load();
      showToast({
        type: "success",
        title: "Success",
        message: "Profile picture removed.",
      });
    } catch (error) {
      handleRequestError(error);
    } finally {
      setBusy(false);
    }
  };

  const onDeleteAccount = async () => {
    if (!user?.id) return;
    if (
      !window.confirm(
        "Delete your account permanently? This cannot be undone."
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await deleteUserService(user.id);
      logout();
      navigate("/login", { replace: true });
    } catch (error) {
      handleRequestError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <h1>Profile</h1>
      </header>

      <section className="panel profile-hero">
        <UserAvatar
          uri={user?.profile_image_url}
          name={formatDisplayName(user?.first_name, user?.last_name)}
          size={88}
        />
        <div>
          <h2>{formatDisplayName(user?.first_name, user?.last_name)}</h2>
          <p className="muted">{user?.email}</p>
          <p className="muted">{user?.phone_number || "No phone"}</p>
          <p className="muted">{user?.permissions?.[0]}</p>
        </div>
      </section>

      <div className="stack gap-md">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
        <button
          type="button"
          className="btn btn-secondary"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          Change photo
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy || !user?.profile_image_url}
          onClick={onRemovePhoto}
        >
          Remove photo
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate("/profile/edit")}
        >
          Edit profile
        </button>
        <button type="button" className="btn btn-ghost" onClick={onLogout}>
          Log out
        </button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy}
          onClick={onDeleteAccount}
        >
          Delete account
        </button>
      </div>
    </div>
  );
};
